import { useState, useEffect } from 'react';

interface FeatureFlags {
  topicPills: boolean;
  topicNavigation: boolean;
  enhancedSearch: boolean;
  graphAnimations: boolean;
  temporalGraphView: boolean;
  graphAnnotations: boolean;
  advancedExport: boolean;
  decisionMode: boolean;
  semanticRelationships: boolean;
  synthesisMode: boolean;
  evolutionMode: boolean;
  quickCaptureEnhancements: boolean;
  batchUpload: boolean;
  slackMessaging: boolean;
}

// Simple feature flag system for gradual rollout
export const useFeatureFlags = (): FeatureFlags => {
  const [flags, setFlags] = useState<FeatureFlags>({
    topicPills: true, // Week 2: Start with 100% for development/testing
    topicNavigation: false, // Week 3: Not yet implemented
    enhancedSearch: false, // Week 4: Not yet implemented
    graphAnimations: true, // Week 7: Graph animations enabled by default
    temporalGraphView: true, // Week 7: Temporal graph view enabled by default
    graphAnnotations: true, // Week 8: Graph annotations enabled
    advancedExport: true, // Week 8: Advanced export enabled
    decisionMode: true, // Week 11: Decision Mode enabled
    semanticRelationships: true, // Week 12: Semantic relationship visualization
    synthesisMode: true, // Week 15: Synthesis Mode enabled
    evolutionMode: true, // Week 14: Evolution Mode enabled
    quickCaptureEnhancements: true, // Week 13: Quick Capture enhancements
    batchUpload: true, // Batch document upload for cold-start
    slackMessaging: true, // Slack-like messaging with DMs, groups, channels
  });

  useEffect(() => {
    // In production, this would:
    // 1. Check user's rollout percentage based on user ID hash
    // 2. Call feature flag service API
    // 3. Handle A/B testing logic

    // For now, check environment variables for simple on/off flags
    const topicPillsEnabled = import.meta.env.VITE_TOPIC_PILLS_ENABLED !== 'false';
    const graphAnimationsEnabled = import.meta.env.VITE_GRAPH_ANIMATIONS_ENABLED !== 'false';
    const temporalViewEnabled = import.meta.env.VITE_TEMPORAL_VIEW_ENABLED !== 'false';
    const annotationsEnabled = import.meta.env.VITE_GRAPH_ANNOTATIONS_ENABLED !== 'false';
    const advancedExportEnabled = import.meta.env.VITE_ADVANCED_EXPORT_ENABLED !== 'false';
    const decisionModeEnabled = import.meta.env.VITE_DECISION_MODE_ENABLED !== 'false';
    const semanticRelationshipsEnabled = import.meta.env.VITE_SEMANTIC_RELATIONSHIPS_ENABLED !== 'false';
    const synthesisModeEnabled = import.meta.env.VITE_SYNTHESIS_MODE_ENABLED !== 'false';
    const evolutionModeEnabled = import.meta.env.VITE_EVOLUTION_MODE_ENABLED !== 'false';
    const quickCaptureEnabled = import.meta.env.VITE_QUICK_CAPTURE_ENHANCED !== 'false';
    const batchUploadEnabled = import.meta.env.VITE_BATCH_UPLOAD_ENABLED !== 'false';
    const slackMessagingEnabled = import.meta.env.VITE_SLACK_MESSAGING_ENABLED !== 'false';

    // Simulate gradual rollout based on user ID if available
    const userId = localStorage.getItem('userId');
    let rolloutEnabled = true;

    if (userId) {
      // Simple hash-based percentage rollout (0-99)
      const hash = userId.split('').reduce((a, b) => {
        a = ((a << 5) - a) + b.charCodeAt(0);
        return a & a;
      }, 0);
      const percentage = Math.abs(hash) % 100;

      // Start with 100% rollout for Week 2 completion
      // In real deployment: 10% -> 25% -> 50% -> 100%
      const rolloutPercentage = parseInt(import.meta.env.VITE_TOPIC_PILLS_ROLLOUT || '100');
      rolloutEnabled = percentage < rolloutPercentage;
    }

    setFlags(prev => ({
      ...prev,
      topicPills: topicPillsEnabled && rolloutEnabled,
      graphAnimations: graphAnimationsEnabled,
      temporalGraphView: temporalViewEnabled,
      graphAnnotations: annotationsEnabled,
      advancedExport: advancedExportEnabled,
      decisionMode: decisionModeEnabled,
      semanticRelationships: semanticRelationshipsEnabled,
      synthesisMode: synthesisModeEnabled,
      evolutionMode: evolutionModeEnabled,
      quickCaptureEnhancements: quickCaptureEnabled,
      batchUpload: batchUploadEnabled,
      slackMessaging: slackMessagingEnabled,
    }));
  }, []);

  return flags;
};

// Hook for checking individual flags
export const useFeatureFlag = (flagName: keyof FeatureFlags): boolean => {
  const flags = useFeatureFlags();
  return flags[flagName];
};